<template>
  <div>
    <a-skeleton v-if="leading" :active="true" avatar :paragraph="{ rows: 4 }" />
    <div v-else>
      <div class="card card-body">
        <div class="row justify-content-xl-center">
          <div class="col-xl-12 col-lg-12">
            <a-page-header :title="$t('menu.profile')"></a-page-header>
          </div>
        </div>

        <!-- MFA Section - First Priority -->
        <a-form class="mb-5">
          <fieldset>
            <legend class="section-legend">
              <a-icon type="safety-certificate" class="legend-icon" />
              {{ $t("mfa.twoFactorAuth") }}
            </legend>

            <!-- Alert if MFA is not enabled -->
            <a-alert
              v-if="!mfaEnabled"
              :message="$t('mfa.securityAlertTitle')"
              type="warning"
              show-icon
              :closable="false"
              class="mfa-alert mb-4"
            >
              <template slot="description">
                <div class="mfa-alert-content">
                  <p class="mb-2">{{ $t("mfa.securityAlertDescription") }}</p>
                  <ul class="mfa-benefits-list">
                    <li>
                      <a-icon type="check-circle" theme="filled" />
                      {{ $t("mfa.benefit1") }}
                    </li>
                    <li>
                      <a-icon type="check-circle" theme="filled" />
                      {{ $t("mfa.benefit2") }}
                    </li>
                    <li>
                      <a-icon type="check-circle" theme="filled" />
                      {{ $t("mfa.benefit3") }}
                    </li>
                  </ul>
                </div>
              </template>
            </a-alert>

            <mfa-settings
              :user-phone="form.getFieldValue('phone')"
              @mfa-status-changed="handleMFAStatusChanged"
            />
          </fieldset>
        </a-form>

        <!-- Basic Information Section -->
        <a-form :form="form" @submit="handleSubmit" class="mb-5">
          <fieldset>
            <legend class="section-legend">
              <a-icon type="user" class="legend-icon" />
              {{ $t("personnel.infoBase") }}
            </legend>
            <div class="row">
              <div class="col-md-3 col-12 mb-3">
                <a-form-item :label="$t('profil.userProfilePhoto')">
                  <div class="d-flex align-items-center">
                    <div class="position-relative mr-3">
                      <div
                        class="kit__utils__avatar kit__utils__avatar--size256"
                        v-if="userPhoto"
                        style="width: 100px; height: 100px"
                        @click="imageModalVisible = true"
                      >
                        <a-avatar
                          shape="square"
                          :src="userPhoto"
                          :size="100"
                          style="width: 100px; height: 100px; cursor: pointer"
                        />
                      </div>
                      <a-avatar
                        v-else
                        shape="square"
                        icon="user"
                        :class="$style.avatar"
                        :size="100"
                        style="width: 100px; height: 100px"
                      />
                    </div>
                    <div class="d-flex flex-column">
                      <a-upload
                        :showUploadList="false"
                        :beforeUpload="beforeUpload"
                        :customRequest="() => false"
                        accept="image/jpeg,image/png"
                      >
                        <a-button
                          type="link"
                          class="mb-1 text-left px-0 hover-outline"
                        >
                          <a-icon type="upload" class="font-size-16" />
                          {{ $t("profil.uploadUserPhoto") }}
                        </a-button>
                      </a-upload>
                      <a-button
                        v-if="userPhoto"
                        type="link"
                        class="text-danger text-left px-0 hover-outline hover-outline-danger"
                        @click="
                          userPhoto = null;
                          deleteAvatar = true;
                          file = null;
                        "
                      >
                        <a-icon type="delete" class="font-size-16" />
                        {{ $t("profil.removeUserPhoto") }}
                      </a-button>
                    </div>
                  </div>
                </a-form-item>
              </div>

              <div class="col-12 col-md-9"></div>

              <div class="col-md-3 col-12">
                <a-form-item :label="$t('profil.nomUser')">
                  <a-input
                    v-decorator="[
                      'username',
                      {
                        rules: [
                          { required: true, message: $t('requis.username') },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>

              <div class="col-md-3 col-12">
                <a-form-item :label="$t('profil.prenom')">
                  <a-input
                    v-decorator="[
                      'firstName',
                      {
                        rules: [
                          { required: false, message: $t('requis.prenom') },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>

              <div class="col-md-3 col-12">
                <a-form-item :label="$t('profil.nom')">
                  <a-input
                    v-decorator="[
                      'lastName',
                      {
                        rules: [{ required: false, message: $t('requis.nom') }],
                      },
                    ]"
                  />
                </a-form-item>
              </div>

              <div class="col-md-3 col-12">
                <a-form-item :label="$t('profil.tel')">
                  <phone-number-input
                    :default-country-code="isKenyan ? 'KE' : 'TN'"
                    v-decorator="[
                      'phone',
                      {
                        rules: [],
                      },
                    ]"
                  />
                </a-form-item>
              </div>

              <div class="col-12">
                <a-progress
                  :stroke-color="{
                    from: '#108ee9',
                    to: '#87d068',
                  }"
                  v-if="progress.value > 0"
                  :percent="progress.value"
                  status="active"
                />
                <a-button
                  type="primary"
                  html-type="submit"
                  :disabled="status"
                  :loading="status"
                  class="mt-2"
                >
                  {{ $t("action.enregistrer") }}
                </a-button>
              </div>
            </div>
          </fieldset>
        </a-form>

        <!-- Password Reset Section -->
        <a-form :form="formPassword" @submit="validatePassword">
          <fieldset>
            <legend class="section-legend">
              <a-icon type="lock" class="legend-icon" />
              {{ $t("action.reset") + " " + $t("login.password") }}
            </legend>

            <div class="row">
              <div class="col-md-4 col-12">
                <a-form-item :label="$t('profil.currentPassword')">
                  <a-input-password
                    type="password"
                    v-decorator="[
                      'currentPassword',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.mdp'),
                          },
                        ],
                      },
                    ]"
                    :placeholder="$t('profil.currentPassword')"
                  />
                </a-form-item>
              </div>

              <div class="col-md-4 col-12">
                <a-form-item :label="$t('profil.NewPwd')">
                  <a-input-password
                    type="password"
                    :validate-status="passwordError() ? 'error' : ''"
                    :help="passwordError() || ''"
                    v-decorator="[
                      'password',
                      {
                        rules: [
                          {
                            validator: validateStrongPassword,
                          },
                        ],
                      },
                    ]"
                    :placeholder="$t('profil.NewPwd')"
                  />
                </a-form-item>
              </div>

              <div class="col-md-4 col-12">
                <a-form-item :label="$t('utilisateur.confirmerMdp')">
                  <a-input-password
                    type="password"
                    v-decorator="[
                      'confirmedPassword',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.confirmation'),
                          },
                          {
                            validator: compareToFirstPassword,
                            message: $t('requis.confirmation'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>

              <div class="col-12">
                <a-button
                  type="primary"
                  html-type="submit"
                  :disabled="statusPassword"
                >
                  {{ $t("action.enregistrer") }}
                </a-button>

                <a-progress
                  class="mb-4"
                  :stroke-color="{
                    from: '#108ee9',
                    to: '#87d068',
                  }"
                  v-show="uploadPercentagePassword > 0"
                  :percent="uploadPercentagePassword"
                  status="active"
                />
              </div>
            </div>
          </fieldset>
        </a-form>
      </div>
    </div>

    <!-- Add modal for viewing avatar -->
    <a-modal
      :visible="imageModalVisible"
      :footer="null"
      :width="800"
      @cancel="imageModalVisible = false"
      centered
    >
      <img
        v-if="userPhoto"
        :src="userPhoto"
        :alt="form.getFieldValue('username')"
        style="width: 100%; height: auto; object-fit: contain"
      />
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapActions } from "vuex";
import { uploadFilesToS3 } from "@/services/uploadService";
import { mapState } from "vuex";
import { isKenyanSchool } from "@/utilities/countrySpecific";
import MFASettings from "@/components/cleanui/system/Auth/MFASettings/index.vue";

export default {
  components: {
    "mfa-settings": MFASettings,
  },
  data() {
    return {
      leading: true,
      userPhoto: null,
      form: this.$form.createForm(this),
      userState: null,
      progress: { value: 0 },
      status: false,
      uploadPercentagePassword: 0,
      statusPassword: false,
      dbName: null,
      deleteAvatar: false,
      file: null,
      imageModalVisible: false,
      mfaEnabled: false,
    };
  },

  mounted() {},
  computed: {
    ...mapState(["settings"]),
    isKenyan() {
      return isKenyanSchool();
    },
  },
  methods: {
    ...mapActions(["setAvatar"]),
    handleSubmit(e) {
      e.preventDefault();
      this.form.validateFields(async (err, values) => {
        if (!err) {
          try {
            this.status = true;
            const data = {
              userName: values.username,
              firstName: !!values.firstName ? values.firstName : "",
              lastName: !!values.lastName ? values.lastName : "",
              phone: !!values.phone ? values.phone : "",
              dbName: this.dbName,
              avatar: null,
              deleteAvatar: this.deleteAvatar,
            };

            if (this.file) {
              const uploadedKeys = await uploadFilesToS3(
                [this.file],
                "manager/avatar",
                this.progress
              );
              data.avatar = uploadedKeys[0];
            }

            const response = await apiClient.patch(
              "/users/" + this.userState.userName + "/avatar",
              data,
              {
                headers: {
                  db: this.dbName,
                },
              }
            );
            if (response.data && response.data.signedAvatarUrl) {
              this.$store.dispatch(
                "user/setAvatar",
                response.data.signedAvatarUrl
              );
              this.userPhoto = response.data.signedAvatarUrl;
            } else if (this.deleteAvatar) {
              this.userPhoto = null;
              this.$store.dispatch("user/setAvatar", null);
            }

            this.$message.success(this.$t("success.utilisateurMSA"));
          } catch (error) {
            this.$message.error(this.$t("error.impoModifi"));
          } finally {
            this.progress = { value: 0 };
            this.status = false;
            this.deleteAvatar = false;
            this.file = null;
          }
        }
      });
    },

    validatePassword(e) {
      e.preventDefault();
      this.formPassword.validateFields(async (err, values) => {
        if (!err) {
          this.statusPassword = true;

          const requestData = {
            ...values,
            socketId: this.$socket.client?.id || null,
          };

          await apiClient
            .patch(
              "/users/" + this.userState.userName + "/rest/password",
              requestData,
              {
                onUploadProgress: (progressEvent) => {
                  this.uploadPercentagePassword = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                },
              }
            )
            .then((data) => {
              this.$message.success(this.$t("success.utilisateurMSA"));
              this.formPassword.setFieldsValue({
                currentPassword: "",
                password: "",
                confirmedPassword: "",
              });
            })
            .catch((error) => {
              this.$message.error(this.$t("error.impoModifi"));
            })
            .finally(() => {
              this.uploadPercentagePassword = 0;
              this.statusPassword = false;
            });
        }
      });
    },

    compareToFirstPassword(rule, value, callback) {
      const form = this.formPassword;
      if (value && value !== form.getFieldValue("password")) {
        callback("Two passwords that you enter is inconsistent!");
      } else {
        callback();
      }
    },

    beforeUpload(file) {
      const isJpgOrPng =
        file.type === "image/jpeg" || file.type === "image/png";
      if (!isJpgOrPng) {
        this.$message.error(this.$t("error.fichiers"));
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error(this.$t("error.image"));
        return false;
      }

      // Create URL for preview
      this.userPhoto = URL.createObjectURL(file);
      this.file = file;
      this.deleteAvatar = false;
      this.$message.success(
        this.$t("success.imageTelecharge", { name: file.name })
      );
      return false; // Prevent default upload behavior
    },

    checkNotStartingWithZero(rule, value, callback) {
      if (value && value.charAt(0) === "0") {
        callback("Phone number should not start with zero");
      } else {
        callback();
      }
    },
    passwordError() {
      const { getFieldError, isFieldTouched } = this.formPassword;
      return getFieldError("password");
    },
    validateStrongPassword(rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t("requis.mdp")));
      } else if (value == this.userState.userName) {
        callback(new Error(this.$t("profil.passwordMustNotMatchUsername")));
      } else if (value.length < 8) {
        callback(new Error(this.$t("profil.passwordMinLength")));
      } else if (!/[A-Z]/.test(value)) {
        callback(new Error(this.$t("profil.passwordMustIncludeUppercase")));
      } else if (!/[a-z]/.test(value)) {
        callback(new Error(this.$t("profil.passwordMustIncludeLowercase")));
      } else if (!/[0-9]/.test(value)) {
        callback(new Error(this.$t("profil.passwordMustIncludeDigit")));
      } else {
        callback();
      }
    },
    async handleMFAStatusChanged(data) {
      // Update MFA status when it changes
      this.mfaEnabled = data?.enabled || false;
      // Optionally refresh user data
      if (data?.enabled) {
        await this.fetchMFAStatus();
      }
    },
    async fetchMFAStatus() {
      try {
        const userState = this.$store.state.user;
        const response = await apiClient.get(
          `/users/${userState.userName}?includeMFA=true`,
          {
            headers: {
              db: "Root",
            },
          }
        );
        this.mfaEnabled = response.data.mfa?.enabled || false;
      } catch (error) {
        console.error("Error fetching MFA status:", error);
        this.mfaEnabled = false;
      }
    },
  },

  watch: {},

  async created() {
    this.formPassword = this.$form.createForm(this, {
      onFieldsChange: (_, changedFields) => {
        this.$emit("change", changedFields);
      },
    });

    this.userState = this.$store.state.user;

    await apiClient
      .get("/users/" + this.userState.userName + "?includeMFA=true", {
        headers: {
          db: "Root",
        },
      })
      .then((result) => {
        this.leading = false;
        setTimeout(() => {
          const data = result.data;
          this.dbName =
            data.buildings.length > 0 ? data.buildings[0].dbName : null;
          if (data.avatar) {
            this.userPhoto = data.avatar;
          }
          this.mfaEnabled = data.mfa?.enabled || false;
          this.form.setFieldsValue({
            username: data.userName,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
          });
        }, 10);
      });
  },
};
</script>

<style lang="scss" module>
@import "../style.module.scss";
</style>

<style lang="scss" scoped>
.subLabel {
  color: #797979;
  font-size: smaller;
}

.hover-outline {
  border: 1px solid transparent;
  padding: 5px 10px !important;

  &:hover {
    border-color: #1890ff;
    background: #fff;
    border-radius: 4px;
  }

  &.hover-outline-danger:hover {
    border-color: #ff4d4f;
  }
}

.section-legend {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #262626;

  .legend-icon {
    font-size: 20px;
    color: #1890ff;
  }
}

.mfa-alert {
  border-left: 4px solid #faad14;
  background: #fffbe6;

  :deep(.ant-alert-message) {
    font-size: 16px;
    font-weight: 600;
    color: #d46b08;
  }

  :deep(.ant-alert-description) {
    color: #595959;
  }
}

.mfa-alert-content {
  p {
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 1.6;
  }
}

.mfa-benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 14px;
    color: #595959;

    .anticon {
      color: #52c41a;
      font-size: 16px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Improve spacing between sections
.mb-5 {
  margin-bottom: 32px;
}

// Improve form field spacing
:deep(.ant-form-item) {
  margin-bottom: 20px;
}

// Dark mode support
[data-kit-theme="dark"] {
  .section-legend {
    color: #e8e8e8;

    .legend-icon {
      color: #1890ff;
    }
  }

  .mfa-alert {
    border-left-color: #faad14;
    background: #2b2111;

    :deep(.ant-alert-message) {
      color: #ffc53d;
    }

    :deep(.ant-alert-description) {
      color: #bfbfbf;
    }
  }

  .mfa-alert-content {
    p {
      color: #bfbfbf;
    }
  }

  .mfa-benefits-list {
    li {
      color: #bfbfbf;

      .anticon {
        color: #52c41a;
      }
    }
  }
}
</style>
